var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Dialog } from "@headlessui/react";
import useSWR from "swr";
import { process } from "../Process";
import { apiFetch, Auth0Fetcher, SingleStat, StaticTable, StyledButton, StyledButtonStyle, } from "ee-react-components";
import { IncidentImpact, Permission, isIncidentActive, } from "ee-nestjs-interfaces";
import { useParams } from "react-router-dom";
import { BackButton } from "../Components/BackButton";
import { charger } from "ee-proto-interfaces";
import { usePermissions } from "../PermissionsContext";
import { IncidentEditDialog } from "../Components/Incidents/IncidentEditDialog";
import { issueLabel, impactLabel, } from "../Components/Incidents/IncidentLabels";
import { TicketLink } from "../Components/Incidents/TicketLink";
function formatDuration(ms) {
    var totalSeconds = Math.floor(ms / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    return [
        hours > 0 ? "".concat(hours, "h") : null,
        minutes > 0 ? "".concat(minutes, "m") : null,
        "".concat(seconds, "s"),
    ]
        .filter(Boolean)
        .join(" ");
}
// Built once, outside the component: StaticTable memoizes on the array's
// identity, and none of these columns depend on the outage being shown.
var INCIDENT_COLUMNS = [
    {
        header: "Start",
        accessorFn: function (row) { return new Date(row.beginning).toLocaleString(); },
    },
    {
        header: "End",
        accessorFn: function (row) {
            return row.end ? new Date(row.end).toLocaleString() : "Ongoing";
        },
        cell: function (info) {
            var end = info.row.original.end;
            return (React.createElement("label", { className: isIncidentActive(end) ? "text-red" : undefined }, end ? info.getValue() : "ONGOING"));
        },
    },
    { header: "Title", accessorFn: function (row) { return row.title; } },
    {
        header: "Impact",
        // A global incident has no site of its own and turns up on every site's
        // outages, so it says which kind it is rather than passing for this site's.
        accessorFn: function (row) {
            var labels = row.impact.map(impactLabel).join(", ");
            return row.siteId == null ? "".concat(labels, " (Global)") : labels;
        },
    },
    {
        header: "Issue",
        accessorFn: function (row) { return row.issue.map(issueLabel).join(", "); },
    },
    {
        header: "OPS Ticket",
        accessorFn: function (row) { return row.opsTicket; },
        cell: function (info) { return React.createElement(TicketLink, { ticket: info.getValue() }); },
    },
];
/**
 * One outage, and the incidents that overlap with it.
 *
 */
export var Outage = function (_a) {
    var _b, _c, _d, _e;
    var isNoc = _a.isNoc;
    var _f = useAuth0(), isAuthenticated = _f.isAuthenticated, isLoading = _f.isLoading, getAccessTokenSilently = _f.getAccessTokenSilently;
    var _g = useParams(), siteId = _g.siteId, chargerId = _g.chargerId, startTime = _g.startTime;
    var permissions = usePermissions();
    var _h = useState(false), creating = _h[0], setCreating = _h[1];
    var closeCreate = useCallback(function () { return setCreating(false); }, []);
    var _j = useSWR(isLoading || !isAuthenticated
        ? null
        : [
            "".concat(process.env.NOC_SVC_URL, "/outages/find/").concat(siteId, "/").concat(chargerId, "/").concat(startTime),
            getAccessTokenSilently,
        ], Auth0Fetcher, { refreshInterval: 60 * 1000 }), details = _j.data, mutate = _j.mutate;
    var getToken = useCallback(function () {
        return getAccessTokenSilently({
            authorizationParams: { audience: process.env.AUTH0_AUDIENCE },
        });
    }, [getAccessTokenSilently]);
    // An outage belongs to exactly one site, so the site-scoped grant is what
    // decides whether an incident can be filed from here — the same check the
    // station's Outages tab is gated on.
    var canWrite = permissions.hasPermissionForSite(Permission.WRITE_NOC, Number(siteId));
    // The equipment the create form offers, fetched only for someone who can
    // actually file one.
    var site = useSWR(isLoading || !isAuthenticated || !canWrite
        ? null
        : "".concat(process.env.CORE_SVC_URL, "/site/detail/").concat(siteId), function (url) {
        return apiFetch(url, { method: "GET", callbacks: { getToken: getToken } });
    }).data;
    // What an incident filed from this outage starts as: this charger, this
    // window, and the impact an outage is by definition. Everything else — the
    // title, the OPS ticket, the issue — is the judgement the form is asking for,
    // so none of it is guessed here.
    var incidentDefaults = useMemo(function () {
        return details == null
            ? undefined
            : {
                beginning: new Date(details.start),
                end: new Date(details.end),
                chargerIds: [details.chargerId],
                impact: [IncidentImpact.CHARGER_OFFLINE],
            };
    }, [details]);
    var submitCreate = useCallback(function (values) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiFetch("".concat(process.env.NOC_SVC_URL, "/incidents"), {
                        method: "POST",
                        body: __assign({ siteId: Number(siteId) }, values),
                        callbacks: { getToken: getToken },
                    })];
                case 1:
                    _a.sent();
                    // Whether the new incident lands in the list below is the correlation's
                    // answer, not this page's assumption, so the outage is re-read rather
                    // than having the incident appended to it.
                    return [4 /*yield*/, mutate()];
                case 2:
                    // Whether the new incident lands in the list below is the correlation's
                    // answer, not this page's assumption, so the outage is re-read rather
                    // than having the incident appended to it.
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getToken, mutate, siteId]);
    return (isNoc && (React.createElement("div", { className: "flex flex-col pl-[32px]" },
        React.createElement(BackButton, null),
        React.createElement("label", { className: "font-N5 text-white mt-[64px]" }, "Outage Details"),
        details && (React.createElement("label", { className: "font-N8 text-black-400 mt-[4px]" }, "".concat(new Date(startTime).toLocaleString()))),
        React.createElement("div", { className: "grid grid-cols-3 mt-[64px] gap-x-[32px] gap-y-[32px] w-fit" },
            React.createElement(SingleStat, { title: "Charger ID", value: "".concat(details === null || details === void 0 ? void 0 : details.chargerId) }),
            React.createElement(SingleStat, { title: "Site ID", value: "".concat(details === null || details === void 0 ? void 0 : details.siteId) }),
            React.createElement(SingleStat, { title: "Ports", value: "".concat(details === null || details === void 0 ? void 0 : details.affectedPorts.join(", ")) }),
            React.createElement(SingleStat, { title: "Start Time", value: (details === null || details === void 0 ? void 0 : details.start) ? new Date(details.start).toUTCString() : "N/A" }),
            React.createElement(SingleStat, { title: "End Time", value: (details === null || details === void 0 ? void 0 : details.end) ? new Date(details.end).toUTCString() : "N/A" }),
            React.createElement(SingleStat, { title: "Duration", value: formatDuration((_b = details === null || details === void 0 ? void 0 : details.duration) !== null && _b !== void 0 ? _b : 0) }),
            (details === null || details === void 0 ? void 0 : details.serialNumber) && (React.createElement(SingleStat, { title: "Serial Number", value: details.serialNumber })),
            (details === null || details === void 0 ? void 0 : details.unavailableReasons) &&
                details.unavailableReasons.length > 0 && (React.createElement(SingleStat, { title: "Unavailable Reasons", value: details.unavailableReasons
                    .map(function (ur) { return charger.unavailableReasonToJSON(ur); })
                    .join(", ") })),
            React.createElement(SingleStat, { title: "Incident Issues", value: (details === null || details === void 0 ? void 0 : details.issues.length)
                    ? details.issues.map(issueLabel).join(", ")
                    : "None" })),
        React.createElement("hr", { className: "border-black-800 max-w-[50%] my-8" }),
        React.createElement("div", { className: "flex flex-col gap-y-[16px] w-fit" },
            React.createElement("label", { className: "font-N5 text-white" }, "Coinciding Incidents"),
            React.createElement("label", { className: "font-N8 text-black-400 max-w-[600px]" }, "Every incident whose window overlaps this outage. Open one to edit it."),
            React.createElement(StaticTable, { data: (_c = details === null || details === void 0 ? void 0 : details.incidents) !== null && _c !== void 0 ? _c : [], columnDefinitions: INCIDENT_COLUMNS, loading: details === undefined, emptyMessage: "No incident covers this outage.", buildRowLink: function (row) { return "/incident/".concat(row.uid); } }),
            canWrite && details != null && (React.createElement(StyledButton, { text: "CREATE INCIDENT FOR THIS OUTAGE", className: "w-fit", style: StyledButtonStyle.Green, onClick: function () { return setCreating(true); } }))),
        React.createElement(Dialog, { open: creating, onClose: closeCreate, className: "relative z-50" },
            React.createElement("div", { className: "fixed inset-0 bg-black/30", "aria-hidden": "true" }),
            React.createElement("div", { className: "fixed inset-0 flex items-center justify-center p-4" },
                React.createElement(Dialog.Panel, { className: "mx-auto" }, creating && (React.createElement(IncidentEditDialog, { defaults: incidentDefaults, chargers: (_d = site === null || site === void 0 ? void 0 : site.chargers) !== null && _d !== void 0 ? _d : [], powernodes: (_e = site === null || site === void 0 ? void 0 : site.powernodes) !== null && _e !== void 0 ? _e : [], onClose: closeCreate, onSubmit: submitCreate }))))))));
};
